import * as AnalyticsData from '~/analytics/analytics-data';
import { EXPERIMENT_TYPES } from '~/constants/campaigns';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { isDynamicYieldEnabled } from '~/lib/dynamic-yield/dynamic-yield-settings';
import * as RudderstackReady from '~/lib/rudderstack/rudderstack-ready-api';

/**
 * Sends an event to Dynamic Yield using their client-side API
 *
 * @param {string} eventName - The name of the event
 * @param {Object} properties - The event properties object
 * @returns {boolean} True if event was sent successfully, false otherwise
 */
export const sendDynamicYieldEvent = (eventName, properties = {}) => {
  try {
    if (!isDynamicYieldEnabled()) {
      DataDogLogs.info('[Allbirds pixel] - sendDynamicYieldEvent - DY disabled in settings', {
        eventName,
        properties,
      });
      return false;
    }

    // Check if Dynamic Yield is available
    if (!window.DY || !window.DY.API) {
      DataDogLogs.warn('[Allbirds pixel] - sendDynamicYieldEvent - DY not available', {
        eventName,
        properties,
      });
      return false;
    }

    // Send the event to Dynamic Yield
    window.DY.API('event', {
      name: eventName,
      properties,
    });

    DataDogLogs.info('[Allbirds pixel] - sendDynamicYieldEvent - Event sent successfully', {
      eventName,
      properties,
    });

    return true;
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - sendDynamicYieldEvent - Error sending DY event',
      { eventName, properties },
      error,
    );
    return false;
  }
};

/**
 * Sends an add-to-cart event to Dynamic Yield
 *
 * @param {Object} params - The event parameters
 * @param {string} params.productId - The product SKU
 * @param {number} params.price - The product price
 * @param {number} params.quantity - The quantity added
 * @param {string} [params.currency='USD'] - The currency code
 * @returns {boolean} True if event was sent successfully
 */
export const sendDynamicYieldAddToCartEvent = ({
  currency = 'USD',
  price,
  productId,
  quantity = 1,
}) => {
  return sendDynamicYieldEvent('Add to Cart', {
    currency,
    dyType: 'add-to-cart-v1',
    productId,
    quantity,
    value: price,
  });
};

/**
 * Sends a remove-from-cart event to Dynamic Yield
 *
 * @param {Object} params - The event parameters
 * @param {string} params.productId - The product SKU
 * @param {number} params.price - The product price
 * @param {number} params.quantity - The quantity removed
 * @param {string} [params.currency='USD'] - The currency code
 * @returns {boolean} True if event was sent successfully
 */
export const sendDynamicYieldRemoveFromCartEvent = ({
  currency = 'USD',
  price,
  productId,
  quantity = 1,
}) => {
  return sendDynamicYieldEvent('Remove from Cart', {
    currency,
    dyType: 'remove-from-cart-v1',
    productId,
    quantity,
    value: price,
  });
};

/**
 * Sends a newsletter subscription event to Dynamic Yield
 *
 * @param {Object} params - The event parameters
 * @param {string} params.email - The user's email address
 * @returns {boolean} True if event was sent successfully
 */
export const sendDynamicYieldNewsletterSubscriptionEvent = ({ email }) => {
  return sendDynamicYieldEvent('Subscribe', {
    dyType: 'subscribe-to-newsletter-v1',
    email,
  });
};

/**
 * Tracks experiment viewed event for client-side Dynamic Yield campaigns (WYSIWYG and Custom Code)
 * This function is called by Dynamic Yield's connector section afterVariationRendered callback
 *
 * @param {string} tagId - The campaign ID from Dynamic Yield
 * @param {string} tagName - The campaign name from Dynamic Yield
 * @param {string} experienceName - The experience name from Dynamic Yield
 * @param {string} experience - The experience ID from Dynamic Yield
 * @param {Array} variations - Array of variation objects from Dynamic Yield
 * @returns {boolean} True if event was triggered successfully
 */
export const trackClientSideCampaignViewed = (
  tagId,
  tagName,
  experienceName,
  experience,
  variations,
) => {
  try {
    if (!isDynamicYieldEnabled()) {
      DataDogLogs.warn(
        '[Allbirds pixel] - trackClientSideCampaignViewed - DY disabled in settings',
        {
          experience,
          experienceName,
          tagId,
          tagName,
          variations,
        },
      );
      return false;
    }

    if (!tagId || !tagName) {
      DataDogLogs.warn(
        '[Allbirds pixel] - trackClientSideCampaignViewed - Missing required campaign data',
        { experience, experienceName, tagId, tagName, variations },
      );
      return false;
    }

    const variation = variations?.[0] || {};
    const experienceNameDecoded = experienceName
      ? decodeURIComponent(experienceName)
      : experienceName;
    const variationNameDecoded = variation.name
      ? decodeURIComponent(variation.name)
      : experienceNameDecoded;

    // Build analytics metadata object matching the format expected by triggerExperimentViewed
    const analyticsMetadata = {
      campaignId: String(tagId),
      campaignName: tagName,
      experienceId: String(experience.id),
      experienceName: experienceNameDecoded,
      variationId: String(variation.id),
      variationName: variationNameDecoded,
    };

    DataDogLogs.info(
      '[Allbirds pixel] - trackClientSideCampaignViewed - Tracking client-side campaign',
      { analyticsMetadata },
    );

    RudderstackReady.rudderanalyticsReadyCallback(() => {
      AnalyticsData.triggerExperimentViewed(
        analyticsMetadata,
        EXPERIMENT_TYPES.WEB_PERSONALIZATION,
      );
    }, RudderstackReady.CALLBACK_NAMES.DYNAMIC_YIELD_CLIENT_SIDE_CAMPAIGN);

    return true;
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - trackClientSideCampaignViewed - Error tracking client-side campaign',
      { experience, experienceName, tagId, tagName, variations },
      error,
    );
    return false;
  }
};
